<template>
  <transition
    :name="transitionName"
    @after-enter="onOpenedHandler"
    @after-leave="onCloseHandler"
  >
    <div
      v-show="visibleValue"
      :class="[contentClass, $attrs.class]"
      :style="[contentStyle, attrStyle]"
      @click="onClickContent"
    >
      <div
        v-if="hasControl"
        v-show="showControl"
        class="popup-control-line"
      />
      <div
        class="popup-content"
        @dragover.prevent
        @drop.prevent
      >
        <slot />
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { createApp, App, getCurrentInstance, StyleValue } from 'vue';
import { Options, Vue, Prop, Watch, Emit } from 'vue-property-decorator';
import overlay from '../overlay/index.vue';
import { ComponentPublicInstance } from '@vue/runtime-core';
import { targetOutsideElement } from '_c/util/dom';

export interface IMouseEventExtension extends MouseEvent {
  path?: Array<HTMLElement>;
}

@Options({
  name: 'VPopup',
  inheritAttrs: false,
  emits: [
    'update:modelValue',
    'close',
    'close-overlay',
    'click-overlay',
  ],
})
export default class VPopup extends Vue {
  @Prop({ default: false }) readonly modelValue: boolean;
  @Prop({ default: 'center' }) readonly position: string;
  @Prop({ default: 0.3 }) readonly duration: number;
  @Prop({ default: false }) readonly round: boolean;
  @Prop({ default: false }) readonly hasControl: boolean;
  @Prop({ default: 1 }) readonly zIndex: number;
  @Prop({ default: null }) readonly overlayBackground: string;
  @Prop({ default: true }) readonly enabledClose: boolean;
  // 如果不需要 overlay 层，那么 `enabledClose: false` 将会失效
  @Prop({ default: true }) readonly needOverlay: boolean;

  overlayComponent?: App;
  overlayInstance?: ComponentPublicInstance;
  showControl = false;

  @Watch('visibleValue', { immediate: true })
  onVisibleChanged(val: boolean): void {
    if (this.overlayInstance?.$) {
      this.overlayInstance.$.props.visible = val;
    }
    if (val) {
      // 如果不需要 overlay，那么就监听非 popup 区域的点击，一旦发生点击，那么就执行关闭动作
      if (!this.needOverlay) {
        // 在事件捕获阶段，去处理，防止一些节点 stop 掉冒泡
        this.$nextTick(() => {
          document.addEventListener('click', this.handlerOutsideClick, true);
        });
      }
    } else {
      document.removeEventListener('click', this.handlerOutsideClick, true);
      this.showControl = val;
    }
  }

  get visibleValue() {
    return this.modelValue;
  }

  set visibleValue(value: boolean) {
    this.$emit('update:modelValue', value);
  }

  get contentClass(): string[] {
    const style = ['v-popup', `popup-${this.position}`];
    if (this.round) {
      style.push('round');
    }
    return style;
  }

  get contentStyle(): Record<string, StyleValue> {
    return {
      'animation-duration': `${this.duration}s`,
      zIndex: `${this.zIndex}` ?? '',
    };
  }

  get attrStyle(): Record<string, StyleValue> {
    const attrStyle = this.$attrs.style;
    if (attrStyle && typeof attrStyle === 'object') {
      return attrStyle as Record<string, StyleValue>;
    }
    return {};
  }

  get transitionName(): string {
    return `popup-slide-${this.position}`;
  }

  @Emit('open')
  onOpenedHandler(): void {
    this.showControl = true;
  }

  @Emit('close')
  onCloseHandler(): void {
    return;
  }

  @Emit('onClickContent')
  onClickContent() {
    return;
  }

  executeClose(): boolean {
    if (this.needOverlay && !this.enabledClose) {
      return false;
    }
    this.visibleValue = !this.visibleValue;
    return true;
  }

  onOverlayClickHandler(): void {
    const canClose = this.executeClose();
    if (canClose) {
      this.$emit('click-overlay');
      this.$emit('close-overlay');
    }
  }

  createOverlay(): void {
    const currentInstance = getCurrentInstance();
    this.overlayComponent = createApp(overlay, {
      duration: this.duration,
      background: this.overlayBackground,
      visible: this.visibleValue,
      zIndex: this.zIndex,
      onClick: this.onOverlayClickHandler,
    });
    Object.assign(this.overlayComponent._context, currentInstance?.appContext);
  }

  handlerOutsideClick(e: MouseEvent) {
    const isOutside = targetOutsideElement(e, this.$el);
    if (isOutside) {
      this.onOverlayClickHandler();
    }
  }

  created(): void {
    if (this.needOverlay) {
      this.createOverlay();
    }
  }

  mounted(): void {
    const el = document.createElement('div');
    if (this.overlayComponent) {
      this.overlayInstance = this.overlayComponent.mount(el);
    }
    document.body.appendChild(el.children[0]);
    document.body.appendChild(this.$el);
  }

  beforeUnmount() {
    try {
      if (this.overlayInstance) {
        document.body.removeChild(this.overlayInstance.$el);
      }
      document.body.removeChild(this.$el);
    } catch (e) {
      // nothing to do;
    }
  }

  unmounted(): void {
    if (this.overlayComponent) {
      this.overlayComponent.unmount();
    }
    document.removeEventListener('click', this.handlerOutsideClick, true);
  }
}
</script>

<style lang="scss" src="./index.scss" scoped></style>
